<template>
  <div
    v-show="fileArray.length"
    class="notif-files"
    :class="{'is-loading': filesError, 'is-error': filesError, 'close': !show, 'open': show, 'introjs-showElement': tour}"
    data-qa="download-files"
    @click="show = !show">
    <template v-if="show">
      <div
        class="notif-rows">
        <div
          v-for="(file, fileIndex) in fileArray"
          :key="`file-${fileIndex}`"
          class="notif-row"
          :class="{'is_error': file.error, 'is_loading': !file.path}"
          :data-qa="`file-${fileIndex}`"
          @click.stop="!file.path || file.loading || file.error ? null : startDownloadFile(file.key)">
          <sButton
            v-if="file.path || file.error"
            class="mr-2"
            :color="file.error ? 'var(--color-error)' : 'var(--color-main-green)'"
            :disabled="file.loading"
            icon="delete"
            small
            type="text"
            @click="deleteFile(file.key)"/>
          <sButton
            v-if="file.retryProps"
            class="mr-2"
            :color="file.error ? 'var(--color-error)' : 'var(--color-main-green)'"
            :disabled="file.loading"
            icon="repeat"
            small
            type="text"
            @click="repeatFile(file.retryProps)"/>
          <sIcon
            v-if="!file.error"
            :color="file.error ? 'var(--color-error)' : 'var(--color-main-green)'"
            line-height="30px"
            size="14"
            :spin="!file.path || file.loading"
            :type="!file.path || file.loading ? 'loading' : 'download'"/>
          <div class="file-name-row">
            <div>
              <div
                class="file-name"
                data-qa="file-name">
                {{ file.title }}
              </div>
              <sProgress
                v-if="file.fileObject && file.fileObject.loadingPercentage"
                class="mb-0"
                :value="file.fileObject.loadingPercentage || 0"/>
            </div>
            <div class="badge"/>
          </div>
        </div>
      </div>
      <div
        class="notif-icon">
        <sIcon
          color="var(--white-color)"
          rotate="180"
          type="arrow"/>
      </div>
    </template>
    <template v-else>
      <span
        class="notif-count"
        :class="{'badge': needBadge}"
        data-qa="notif-count-badge">{{ fileArray.length }}</span>
      <sIcon
        v-if="filesLoading"
        class="notif-icon"
        color="var(--color-main-green)"
        size="36"
        spin
        type="loading"/>
    </template>
  </div>
</template>
<script>
import { debounce } from 'toolkit/utils/util'
import { storageMixin } from 'mixins'
import functionNormalizes from 'mixins/functionNormalizes'
import { eventBus } from '@/main'

// Бекенд серіалізує errorCode по-різному залежно від флоу (спостережено як PascalCase,
// напр. "AccessDenied", хоча документація описувала SCREAMING_SNAKE_CASE) — порівнюємо
// нормалізованими, щоб не залежати від конкретного стилю написання.
const normalizeErrorCode = code => code?.replace(/[^a-z0-9]/gi, '').toLowerCase()

const templateFileProps = {
  show: true,
  path: null,
  error: false,
  startDownload: null
}

/**
 * Индикатор отложенных загрузок файлов
 */
export default {
  name: 'downloadFiles',
  mixins: [ storageMixin, functionNormalizes ],
  data() {
    return {
      files: {},
      show: false,
      title: null,
      tour: false
    }
  },
  methods: {
    /**
     * Удаление файла из списка
     */
    deleteFile(key) {
      this.$delete(this.files, key)
    },
    repeatFile(retryProps) {
      if (retryProps?.method) {
        const { method, methodProps } = retryProps

        if (this.$notifier[method] && typeof this.$notifier[method] === 'function') {
          this.$notifier[method](...Object.values(methodProps))
        } else {
          this.$notice.error({
            title: this.$t('Common.ErrorTitle'),
            message: 'Немиожливо виконати дію',
            showClose: true,
            progress: true,
          })
        }
      }
    },
    /**
     * При закрытии сообщения меняем признак. Что б потом отобразить заново
     */
    hideNotification(key) {
      if (this.files[key] && this.files[key].show) {
        this.files[key].show = false
      }
    },
    /**
     * При старте формирования файла добавляем свойство и отображаем сообщение с индикацией
     */
    startFileDownload(params) {
      if (this.$notice.instances.find(i => i.id === params.key)) {
        this.$notice.closeById(params.key)
      }

      const fileProps = this.$_.cloneDeep(templateFileProps)
      fileProps.key = params.key
      fileProps.title = params.title || this.$t('Common.Loading')
      fileProps.startDownload = new Date()
      fileProps.loading = false
      fileProps.fileObject = {}

      this.$set(this.files, params.key, fileProps)
      this.$notice.info({
        id: params.key,
        title: fileProps.title,
        message: this.$t('Common.ArchiveTenderNotificationTitle'),
        dangerouslyUseHTMLString: true,
        duration: 0,
        loading: true,
        onClose: () => this.hideNotification(params.key)
      })
      this.$nextTick(() => {
        this.showTour()
      })
    },
    /**
     * Ошибка загрузки файла
     */
    errorhDownload(params) {
      if (!params?.key || !this.files[params.key]) {
        return
      }
      const retryProps = params.method ? {
        method: params.method,
        methodProps: params.methodProps
      } : null

      this.$set(this.files[params.key], 'error', true)
      this.$set(this.files[params.key], 'loading', false)
      if (retryProps) {
        this.$set(this.files[params.key], 'retryProps', retryProps)
      }

      const methodRetry = params.method ? params.method : null

      const errorMessages = {
        [normalizeErrorCode('REPORT_GENERATION_VALIDATION_PERIOD_ERROR')]: this.$t('Common.ReportGenerationPeriodError'),
        [normalizeErrorCode('REPORT_GENERATION_NOT_FOUND_ERROR')]: this.$t('Common.ReportGenerationNotFoundError'),
        [normalizeErrorCode('ACCESS_DENIED')]: this.$t('Common.ReportGenerationAccessDeniedError'),
      }

      const notifyParams = {
        id: params.key,
        type: 'warning',
        title: params.title || this.files[params.key].title || '',
        message: errorMessages[normalizeErrorCode(params.errorCode)] || 'Помилка завантаження файла</br>Спробуйте ще раз',
        dangerouslyUseHTMLString: true,
        duration: 10,
        loading: false,
        showClose: true,
        clickText: methodRetry ? this.$t('Purchasing.Repeat') : false,
        closeText: this.$t('Purchasing.Remove'),
        onClick: retryProps ? () => {
          this.repeatFile(retryProps)
          this.$notice.closeById(params.key)
        } : null,
        onCloseBtnClick: () => {
          this.deleteFile(params.key)
          this.$notice.closeById(params.key)
        }
      }

      this.$notice.update(notifyParams, params.key)
    },
    /**
     * Завершение формирования файла
     */
    async finishDownload(params) {
      let title = ''
      if (!this.files[params.key]) {
        return
      }

      this.files[params.key].path = params.filePath
      this.files[params.key].error = false
      this.files[params.key].retryProps = null
      title = params.title || this.files[params.key].title
      this.files[params.key].type = params.fileType

      // Для архива имя задаётся через query (?fileName=), т.к. кросс-доменный download игнорирует атрибут download.
      // Имя берём из title (оно в массиве files и переживает перезагрузку через localStorage).
      if (params.fileType === 'archive') {
        const name = this.$_normalizeArchiveName(this.files[params.key].title)
        this.files[params.key].path = `${params.filePath}?fileName=${encodeURIComponent(name + '.zip')}`
      }

      const notifyParams = {
        id: params.key,
        title: title,
        type: 'info',
        message: this.$t('Common.DownloadFileFinish'),
        dangerouslyUseHTMLString: true,
        duration: 10,
        loading: false,
        showClose: true,
        onClose: () => {
          /** Возможно, нужно будет удалять файлы при закрытии нотификации */
          // this.$delete(this.files, params.key)
        }
      }

      if (this.files[params.key]?.show) {
        this.$notice.update(notifyParams, params.key)
      } else {
        this.$notice.info(notifyParams)
      }
      this.show = true

      this.$nextTick(() => {
        this.showTour(false)
      })
    },
    /**
     * Старт загрузки файла
     */
    startDownloadFile: debounce(async function (key) {
      if (this.files[key] && this.files[key].title && !this.files[key].loading) {
        const uri = window.config.GrapgQlUrl + this.files[key].path
        let a = document.createElement('a')
        document.body.appendChild(a)
        a.style = 'display: none'
        a.href = uri
        a.download = this.files[key].title
        a.click()
        setTimeout(() => {
          document.body.removeChild(a)
          this.deleteFile(key)
        }, 0)
      } else {
        this.$notice.closeById(key)
        this.deleteFile(key)
      }
    }, 250),
    /**
     * Сохраняем текущие файлы
     */
    savePropsToLocalStorage() {
      const result = {}
      Object.entries(this.files).forEach((value) => {
        if (value[1].path) {
          result[value[0]] = value[1]
        }
      })
      this.$_savePropToDBStorage('download-files.list', result)
    },
    /**
     * Применяем ранее сохраненые файлы
     */
    getPropsToLocalStorage() {
      this.$_getPropFromDBStorage('download-files.list', {})
        .then((result) => {
          const getMinDiff = (startDate, endDate) => {
            const days = parseInt((endDate - startDate) / (1000 * 60 * 60 * 24))
            const hours = parseInt(Math.abs(startDate - endDate) / (1000 * 60 * 60) % 24)
            const minutes = parseInt(Math.abs(startDate - endDate) / (1000 * 60) % 60)
            return (days * 24 * 60) + (hours * 60) + minutes
          }

          Object.entries(result).forEach((value) => {
            const minDiff = getMinDiff(new Date(value[1].startDownload), new Date())
            if (minDiff < 180) {
              this.$set(this.files, value[0], value[1])
            }
          })
        })
    },
    async showTour(start = true) {
      this.tour = false
      const key = `download-files_tour.${start ? 'start' : 'finish'}`
      await this.$_getPropFromDBStorage(key, false)
        .then(async (viewedTour) => {
          if (viewedTour) { return }
          this.tour = true
          const tour = [
            {
              dataKey: 'download-files',
              title: start ? this.$t('Common.FileFormation') : this.$t('Common.FileFormationSuccess'),
              text: start ? this.$t('Common.FileFormationText') : this.$t('Common.FileFormationSuccessText'),
              step: 5,
            }
          ]
          eventBus.$emit('startTour', tour)
          this.$_savePropToDBStorage(key, true, new Date().setDate(new Date().getDate() + 180))
        })
    }
  },
  computed: {
    fileArray() {
      if (!this.files) { return [] }
      return Object.values(this.files)
    },
    filesLoading() {
      if (!this.fileArray?.length) { return false }
      return !!this.fileArray.filter(i => !i.path && !i.error)?.length
    },
    filesError() {
      if (!this.fileArray?.length) { return false }
      return !!this.fileArray.filter(i => i.error)?.length
    },
    needBadge() {
      if (!this.fileArray?.length) { return false }
      return !!this.fileArray.filter(i => i.path)?.length

    }
  },
  watch: {
    files: {
      deep: true,	// Коллбэк будет вызываться каждый раз, когда изменяется любое из свойств наблюдаемого объекта, независимо от глубины их вложенности
      handler() {
        this.savePropsToLocalStorage()
      }
    }
  },
  mounted() {
    window.addEventListener('beforeunload', this.savePropsToLocalStorage)
    this.getPropsToLocalStorage()
    this.$root.$on('startFileDownload', this.startFileDownload)
    this.$root.$on('finishFileDownload', this.finishDownload)
    this.$root.$on('errorFileDownload', this.errorhDownload)
  },
  beforeDestroy() {
    this.savePropsToLocalStorage()
    window.removeEventListener('beforeunload', this.savePropsToLocalStorage)
    this.$root.$off('startFileDownload', this.startFileDownload)
    this.$root.$off('finishFileDownload', this.finishDownload)
    this.$root.$off('errorFileDownload', this.errorhDownload)
  },
}
</script>
<style scoped lang="scss">
%badge {
  border-radius: 4.5px;
  height: 9px;
  min-width: 0;
  padding: 0;
  width: 9px;
  min-width: 9px;
  background-color: #f44336 !important;
  border-color: #f44336 !important;
}

.notif-files {
	position: fixed;
	right: 10px;
	bottom: 10px;
	z-index: 1001;
  max-width: 90%;
	background: var(--black-color);
	transition: width 0.25s ease-in-out;
  box-shadow: 0px 3px 10px !important;
}
.notif-files.close {
	border-radius: 50px;
	min-height: 52px;
	width: 52px;
	display: flex;
	cursor: pointer;
	.notif-icon {
		position: absolute;
		right: 8px;
		bottom: 8px;
	}
	.notif-count {
		background: var(--v-secondary__green_chosen-base);
		border-radius: 50px;
		min-width: 32px;
		max-width: 32px;
		min-height: 32px;
		max-height: 32px;
		line-height: 32px;
		font-size: 13px;
		font-weight: 600;
		margin: auto 10px auto auto;
		transition: all 0.5s;
		&.badge:after {
			@extend %badge;
      content: '';
      position: absolute;
		}
	}
  &.is-error .notif-count {
    background: var(--color-error);
  }
	&.is-loading .notif-count {
		min-width: 20px;
		max-width: 20px;
		min-height: 20px;
		max-height: 20px;
		line-height: 1.5;
		font-size: 14px;
		font-weight: 500;
    margin: auto 16px auto auto;
	}
}
.notif-files.open {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 11px 16px 11px 12px;
	border-radius: 30px;
	.notif-icon {
		width: 40px;
		cursor: pointer;
		padding-left: 15px;
		padding-bottom: 5px;
	}
	.notif-rows {
		overflow: hidden;
    overflow-y: auto;
    max-height: 250px;
	}
	.notif-row {
		display: flex;
		margin-bottom: 10px;
		cursor: pointer;
    overflow: hidden;
    &:last-child {
       margin-bottom: 0px;
    }
		&.is_loading {
			cursor: no-drop;
			.file-name {
				color: var(--v-secondary__green_not_active-base);
			}
		}
    &.is_error .file-name {
      color: var(--color-error);
    }
    &:not(.is_loading) .badge {
      @extend %badge;
    }
	}
	.file-name {
    line-height: 30px;
    text-align: left;
		margin-left: 8px;
		color: var(--color-main-green);
    overflow: hidden;
    text-overflow: ellipsis;
		white-space: nowrap;
    // width: calc(100% - 10px);
	}
  .file-name-row {
    display: flex;
    max-width: 100%;
    overflow: hidden;
  }
}
::v-deep .btn-file-delete-icon {
  fill: var(--color-main-green);
}
</style>
